// React
import { useState, useEffect, useContext, useMemo } from "react";
import { Disclosure, Menu } from "@headlessui/react";
import { useLocation, useNavigate, NavLink } from "react-router-dom";
//Services
import { GET, LOGOUT } from "../../service/CRUDService";
// Constants
import EntityConstants from "../../configs/EntityConstants";
// Store
import AuthContext from "../../store/auth-context";
import { pickDefaultLanguage, useBranding } from "../../store/branding-context";
// React Icons
import { BiLogOutCircle } from "react-icons/bi";
import Toast from "../../common/Toast/Toast";
import { useTranslation } from "react-i18next";
import AppConstants from "../../configs/AppConstants";
import { googleLogout, useGoogleOneTapLogin } from "@react-oauth/google";
import { GLogin, MLogin } from "../../service/OAuthLogin";
import ProfileIcon from "../../resources/icons/profile-circle.svg";
import WalletIcon from "../../resources/icons/Wallet.svg";
import ReloadIcon from "../../resources/icons/Reload.svg";
import { Home, LanguagesIcon } from "lucide-react";
import { automationTestIds } from "../../testing/automationTestIds";
import { getMicrosoftOAuthRedirectUri } from "../../utils/microsoftOAuthRedirectUri";
import { getProfilePhotoDisplaySrc } from "../../utils/profilePhoto";
import OrganisationBrandLogo from "../OrganisationBrandLogo/OrganisationBrandLogo";

//main NavBar component
export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const { organisationData } = useBranding();
  const home = "/";
  const [code, setCode] = useState("");
  const [isRotating, setIsRotating] = useState(false);
  const [isPrePaid, setIsPrePaid] = useState(false);

  const hasUserDetailsChanged = (nextUserDetails) =>
    JSON.stringify(nextUserDetails) !== JSON.stringify(authContext.userDetails);

  const organisationName = organisationData?.organisation_name?.trim() || "";

  const navbarProfilePhotoSrc = getProfilePhotoDisplaySrc(
    authContext.userDetails?.profile_photo,
    authContext.profilePhotoCache,
  );

  const handleReloadClick = async () => {
    setIsRotating(true);
    setTimeout(() => setIsRotating(false), 1000);
    const didRefresh = await handleCurrentUser();
    if (didRefresh) {
      Toast({
        type: "success",
        title: t("navbar:credit_balance_refresh_header"),
        message: t("navbar:credit_balance_refresh_description"),
      });
    }
  };

  const isUserAuthenticated =
    authContext.isLoggedIn || !!localStorage.getItem("auth_token");

  useGoogleOneTapLogin({
    disabled: isUserAuthenticated,
    onSuccess: async (credentialResponse) => {
      const login = await GLogin(credentialResponse, "success");
      if (login === true) {
        authContext.login() && authContext.googlelogin();
      }
    },
    onError: (error) => GLogin(error, "failed"),
    googleAccountConfigs: {
      client_id: AppConstants.CLIENT_ID,
    },
  });

  const orgLanguages = organisationData?.languages;
  const languages = useMemo(() => {
    if (orgLanguages) {
      return Object.entries(orgLanguages).map(([code, name]) => ({
        value: code,
        label: name,
      }));
    }
    return [{ value: "en", label: "English" }];
  }, [orgLanguages]);

  const [language, setLanguage] = useState(null);

  const logout = async () => {
    try {
      authContext.isGoogleLogin && googleLogout(); // ONLY DO IT IF GOOGLE LOGIN IS USED //! NOW DOING IT ALL TYPES OF LOGIN
      const response = await LOGOUT(EntityConstants.LOGOUT);

      if (response.ok) {
        authContext.createUserProfileDetails({});
        localStorage.clear();
        authContext.logout();
        authContext.createUserProfile(null);
        authContext.createTenantName(null);
        authContext.createTenantId(null);
        location.pathname.startsWith("/issuer") && navigate("/");
        location.pathname === "/" && window.location.reload();
        if (location.pathname === "/designs") {
          navigate("/");
          window.location.reload();
        }
      }
    } catch (error) {
      Toast({
        type: "error",
        title: "Logout Failed !",
        message: error,
      });
    }
  };

  const handleCurrentUser = async () => {
    try {
      const response = await GET(EntityConstants.CURRENT_USER);

      if (response.ok) {
        const data = await response.json();
        const permissions = hasUserDetailsChanged(data)
          ? await authContext.refreshUserSession(data)
          : await authContext.syncUserPermissions();

        if (!permissions) {
          return false;
        }

        authContext.createUserProfile(data.user_profile_id);
        authContext.createTenantName(data.tenant_name);
        authContext.createTenantId(data.tenant_id);
        setIsPrePaid(data.mode_of_payment === "pre_paid");
        return true;
      }
      return false;
    } catch (error) {
      // Handle unexpected errors
      console.error("Failed to fetch current user:", error);
      Toast({
        type: "error",
        title: "Error!",
        message: "An unexpected error occurred. Please try again.",
      });
      return false;
    }
  };

  const handleMicrosoftRedirect = async () => {
    try {
      const codeVerifier = sessionStorage.getItem("pkce_code_verifier");
      if (!codeVerifier) {
        Toast({
          type: "error",
          title: "Error",
          message: t("navbar:microsoft_pkce_missing"),
        });
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname + window.location.hash,
        );
        setCode("");
        return;
      }

      const redirectUri = getMicrosoftOAuthRedirectUri();

      const response = await fetch(EntityConstants.MICROSOFT_AUTH_CODE, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          client_id: AppConstants.MICROSOFT_CLIENT_ID,
          scope: AppConstants.MICROSOFT_SCOPE,
          code: code,
          redirect_uri: redirectUri,
          grant_type: AppConstants.MICROSOFT_GRANT_TYPE,
          code_verifier: codeVerifier,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const loginStatus = await MLogin(data.access_token);
        if (loginStatus) {
          authContext.login();
          authContext.microsoftLogin();
          sessionStorage.removeItem("pkce_code_verifier");
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname + window.location.hash,
          );
        }
      } else {
        let message = t("navbar:microsoft_token_failed");
        try {
          const errorData = await response.json();
          console.error("Failed to fetch access token: ", errorData);
          if (errorData?.error_description) {
            message = errorData.error_description;
          }
        } catch {
          console.error(
            "Failed to fetch access token, status:",
            response.status,
          );
        }
        sessionStorage.removeItem("pkce_code_verifier");
        setCode("");
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname + window.location.hash,
        );
        Toast({
          type: "error",
          title: "Error",
          message: message,
        });
      }
    } catch (error) {
      console.error("Error during token exchange: ", error);
      sessionStorage.removeItem("pkce_code_verifier");
      setCode("");
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname + window.location.hash,
      );
      Toast({
        type: "error",
        title: "Error",
        message: t("navbar:microsoft_token_failed"),
      });
    }
  };

  const handleLanguageChange = (selectedOption) => {
    setLanguage(selectedOption);
  };

  useEffect(() => {
    if (orgLanguages && languages.length > 0) {
      const savedLang =
        authContext.selectedLanguage ||
        localStorage.getItem("selectedLanguage");
      const match = languages.find((l) => l.value === savedLang);
      const portalDefault = pickDefaultLanguage(languages.map((l) => l.value));
      const fallback = languages.find((l) => l.value === portalDefault);
      setLanguage(match || fallback || languages[0]);
    }
  }, [orgLanguages, languages]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setCode(urlParams.get("code"));
  }, []);

  useEffect(() => {
    code && handleMicrosoftRedirect();
  }, [code]);

  useEffect(() => {
    const path = location.pathname;

    // Check if the path is not equal to "/" and doesn't start with "/discover/"
    if (!path.startsWith("discover/")) {
      // Handle the redirect or any other action you want to perform
      //if check for auth_token if exists then call handleCurrentUser().
      localStorage.getItem("auth_token") && handleCurrentUser();
    }
    if (
      path.startsWith("/issuer/certificate/") !== true &&
      path.startsWith("/certificate/") !== true
    ) {
      //clear these on any other page apart from certificate/ page
      sessionStorage.removeItem("details_form_data");
      sessionStorage.removeItem("certificate_title");
    }
  }, [authContext.isLoggedIn, location.pathname]);

  useEffect(() => {
    if (authContext.isGoogleLogin) {
      localStorage.getItem("auth_token") && handleCurrentUser();
    }
  }, [authContext.isGoogleLogin]);

  useEffect(() => {
    if (language?.value) {
      i18n.changeLanguage(language.value);
      authContext.setSelectedLanguage(language.value);
      localStorage.setItem("selectedLanguage", language.value);
      document.documentElement.lang = language.value;
    }
  }, [language?.value, i18n]);

  return (
    <div className="sticky top-0">
      <Disclosure
        as="nav"
        className="bg-cfSecondary border-b-[1.5px]"
        id={automationTestIds.navbar}
      >
        {() => (
          <>
            <div className="mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex h-[5rem] items-center justify-between">
                <NavLink
                  to={home}
                  id={automationTestIds.navbarOrgLink}
                  aria-label={t("navbar:home")}
                  className={
                    "flex items-center hover:text-inherit hover:no-underline focus:outline-none cursor-auto"
                  }
                >
                  <OrganisationBrandLogo
                    alt="organisationLogo"
                    className="h-16 w-auto max-w-[220px] object-contain"
                    skeletonClassName="h-16 w-40 max-w-[220px]"
                    testId={automationTestIds.navbarOrgLink}
                    fallback={
                      organisationName ? (
                        <span
                          id={automationTestIds.navbarOrgNameFallback}
                          className="max-w-[220px] truncate text-xl font-semibold text-cfHeadingText"
                        >
                          {organisationName}
                        </span>
                      ) : (
                        <Home
                          id={automationTestIds.navbarOrgHomeFallback}
                          className="h-7 w-7 text-cfPrimary"
                          aria-hidden="true"
                        />
                      )
                    }
                  />
                </NavLink>
                <div className="flex items-center w-[65%] gap-2 md:gap-5 justify-end">
                  {authContext.isLoggedIn && isPrePaid && (
                    <div className="group relative flex max-w-max drop-shadow-xl flex-col items-center justify-center">
                      <img
                        className="cursor-pointer"
                        src={WalletIcon}
                        height={35}
                        width={35}
                        alt=""
                      />
                      <div
                        className={`absolute left-1/2 top-[2.2rem] ml-auto mr-auto min-w-max -translate-x-1/2 scale-0 transform rounded-lg px-3 py-2 text-xs font-medium transition-all duration-500 group-hover:scale-100`}
                      >
                        <div className="flex flex-col items-center">
                          <div
                            className="relative h-0 w-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent"
                            style={{ borderBottomColor: "#4F4F4F" }}
                          >
                            <div
                              className="absolute top-[2px] left-[-4px] h-0 w-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent"
                              style={{ borderBottomColor: "#ffffff" }}
                            ></div>
                          </div>
                          <div
                            className={`rounded px-2 py-3 text-xs border border-gray-900 text-black whitespace-normal break-words max-w-[275px] md:max-w-[400px]`}
                            style={{ backgroundColor: "#ffffff" }}
                          >
                            <div className="flex items-center ">
                              <div className="w-[22%] text-center text-lg text-cfPrimaryButton font-bold cursor-default">
                                {authContext.userDetails.available_credits
                                  ? authContext.userDetails.available_credits
                                  : 0}
                              </div>
                              <div className="flex flex-col w-[78%] gap-2 ml-1 cursor-default">
                                <div className="flex h-5 items-center gap-3">
                                  <div className="text-[#4F5768] font-bold text-base">
                                    {t("navbar:credit_balance")}
                                  </div>
                                  <div>
                                    <button
                                      type="button"
                                      className="focus:outline-none"
                                      onClick={handleReloadClick}
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === "Enter" ||
                                          e.key === " "
                                        ) {
                                          handleReloadClick();
                                        }
                                      }}
                                    >
                                      <img
                                        src={ReloadIcon}
                                        height={14}
                                        width={14}
                                        alt="Reload Icon"
                                        className={`cursor-pointer transition-transform ${
                                          isRotating ? "animate-rotate" : ""
                                        }`}
                                      />
                                    </button>
                                  </div>
                                </div>
                                <div className="flex justify-start items-center text-[#4F5768]">
                                  {t("navbar:credit_balance_description")}
                                </div>
                              </div>
                              {/* <div className="bg-cfPrimaryButton cursor-pointer px-[0.5rem] py-[0.7rem] rounded-lg font-semibold text-md text-white">
                                Buy Credits
                              </div> */}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center">
                    {authContext.isLoggedIn &&
                    Object.values(authContext.userDetails).length !== 0 ? (
                      <Menu as="div" className="relative">
                        <div>
                          <Menu.Button
                            className="flex max-w-xs items-center text-md text-cfNavBarText focus:outline-none pr-4"
                            id={automationTestIds.navbarUserMenuButton}
                          >
                            {navbarProfilePhotoSrc ? (
                              <span className="flex items-center gap-1">
                                <img
                                  key={`navbar-profile-${authContext.profilePhotoCache}`}
                                  className="h-8 w-8 rounded-full object-cover"
                                  src={navbarProfilePhotoSrc}
                                  alt=""
                                />
                                <p>{authContext.userDetails?.first_name}</p>
                                <p>{authContext.userDetails?.last_name}</p>
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <img
                                  src={ProfileIcon}
                                  alt=""
                                  srcSet=""
                                  className="text-gray-400 w-7"
                                />
                                <p>{authContext.userDetails?.first_name}</p>
                                <p>{authContext.userDetails?.last_name}</p>
                              </span>
                            )}
                          </Menu.Button>
                        </div>
                        <Menu.Items
                          className="flex flex-col text-gray-500 absolute right-0 z-10 mt-2 px-2 text-md w-max origin-top-right rounded-md bg-cfSecondary py-1 shadow-lg ring-1 ring-cfSecondary ring-opacity-5 focus:outline-none"
                          id={automationTestIds.navbarUserMenuItems}
                        >
                          <div>
                            {languages.length > 1 && (
                              <>
                                <div className="flex items-center pl-2">
                                  <span
                                    className="inline-flex items-center"
                                    id={automationTestIds.navbarLanguagesIcon}
                                  >
                                    <LanguagesIcon className="text-gray-600 w-5 h-5" />
                                  </span>
                                  <select
                                    value={language?.value || "en"}
                                    id={automationTestIds.navbarLanguageSelect}
                                    onChange={(e) => {
                                      const selected = languages.find(
                                        (l) => l.value === e.target.value,
                                      );
                                      handleLanguageChange(selected);
                                    }}
                                    className="language-dropdown border-none w-full"
                                  >
                                    {languages.map((lang) => (
                                      <option
                                        key={lang.value}
                                        value={lang.value}
                                      >
                                        {lang.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <hr />
                              </>
                            )}
                            <Menu.Item>
                              {({ active }) => (
                                <NavLink
                                  to="/issuer/profile"
                                  id={automationTestIds.navbarProfileLink}
                                  className={`hover:no-underline hover:text-gray-600 text-cfNavBarText flex w-full items-center gap-1 px-2 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-inset ${
                                    active ? "bg-gray-100" : ""
                                  }`}
                                >
                                  <div className="w-4 h-4 rounded-full flex items-center justify-center">
                                    <img
                                      key={`navbar-menu-profile-${authContext.profilePhotoCache}`}
                                      src={navbarProfilePhotoSrc || ProfileIcon}
                                      alt=""
                                      srcSet=""
                                      className="w-4 h-4 rounded-full object-cover"
                                    />
                                  </div>
                                  &nbsp;
                                  {authContext.userDetails?.first_name}&nbsp;
                                  {authContext.userDetails?.last_name}
                                </NavLink>
                              )}
                            </Menu.Item>
                            <hr />
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  type="button"
                                  id={automationTestIds.navbarSignOut}
                                  className={`flex w-full items-center gap-1 hover:text-gray-600 text-cfNavBarText bg-transparent px-2 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-inset ${
                                    active ? "bg-gray-100" : ""
                                  }`}
                                  onClick={logout}
                                >
                                  <BiLogOutCircle />
                                  <p className="ml-1">{t("navbar:signout")}</p>
                                </button>
                              )}
                            </Menu.Item>
                          </div>
                        </Menu.Items>
                      </Menu>
                    ) : (
                      <div className="flex items-center gap-3 justify-between">
                        {languages.length > 1 && (
                          <Menu as="div" className="relative">
                            <Menu.Button
                              className="flex items-center justify-center p-2 rounded-md hover:bg-gray-100 transition-colors"
                              id={automationTestIds.navbarLanguageMenuButton}
                            >
                              <span
                                className="inline-flex"
                                id={automationTestIds.navbarLanguagesIcon}
                              >
                                <LanguagesIcon className="h-5 w-5 text-gray-600" />
                              </span>
                            </Menu.Button>
                            <Menu.Items
                              className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5"
                              id={automationTestIds.navbarLanguageMenuItems}
                            >
                              <div className="">
                                {languages.map((lang) => (
                                  <Menu.Item key={lang.value}>
                                    {({ active }) => (
                                      <button
                                        className={`${
                                          active
                                            ? "bg-gray-100 text-gray-900"
                                            : "text-gray-700"
                                        } ${
                                          language?.value === lang.value
                                            ? "bg-blue-50 text-blue-600 font-medium"
                                            : ""
                                        } flex w-full items-center rounded-md px-3 py-2 text-sm`}
                                        onClick={() =>
                                          handleLanguageChange(lang)
                                        }
                                      >
                                        {lang.label}
                                      </button>
                                    )}
                                  </Menu.Item>
                                ))}
                              </div>
                            </Menu.Items>
                          </Menu>
                        )}

                        <button
                          type="button"
                          id={automationTestIds.navbarSignUpTrigger}
                          className="hidden sm:!flex shrink-0 items-center justify-center font-CfPrimaryFont text-xs sm:text-sm font-semibold !text-cfPrimary hover:!text-cfPrimaryDark px-3 sm:px-4 py-2 min-h-[2.9rem] rounded-xl text-center leading-snug transition-colors duration-200 focus:outline-none border-none"
                          onClick={() => {
                            authContext.chooseSignUp_SignIn(false);
                            authContext.setTriggerLoginModuleToggleValue(true);
                          }}
                        >
                          <span>{t("navbar:sign_up_to_proceed")}</span>
                        </button>
                        <div>
                          <button
                            type="button"
                            id={automationTestIds.navbarSignInTrigger}
                            className="flex items-center justify-center font-semibold text-cfSecondary bg-cfPrimary hover:bg-cfPrimaryDark px-4 py-2.5 min-w-[5.5rem] h-[2.9rem] rounded-xl whitespace-nowrap focus:outline-none"
                            onClick={() => {
                              authContext.chooseSignUp_SignIn(true);
                              authContext.setTriggerLoginModuleToggleValue(
                                true,
                              );
                            }}
                          >
                            <span className="text-base">
                              {t("navbar:signin")}
                            </span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </Disclosure>
    </div>
  );
}
